import type { ChangeEvent } from 'react'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { AxiosError } from 'axios'

import { Alert } from '@gto/ui/alert'
import { Button } from '@gto/ui/button'
import { Dialog } from '@gto/ui/dialog'
import { Divider } from '@gto/ui/divider'
import { Paper } from '@gto/ui/paper'

import {
  type RedemptionOption,
  useRedemptionsRedemptionOptionsGtoWizardRedeemCreate,
} from '@/api/gen'
import { TextInput } from '@/components/text-input'
import { useEdgeBalance } from '@/hooks/use-edge-balance'
import { useFormValidation } from '@/hooks/use-form-validation'
import { getAxiosErrorMessage } from '@/utils/helpers'
import type { RedeemSubscriptionFormData } from '@/utils/validation'
import { redeemSubscriptionSchema } from '@/utils/validation'

import { GtoSubscriptionCardHeader } from './gto-subscription-card-header'

export type RedeemSubscriptionModalProps = {
  open: boolean
  onClose: () => void
  plan: RedemptionOption
}

export const RedeemSubscriptionModal = ({ open, onClose, plan }: RedeemSubscriptionModalProps) => {
  const { formatMessage } = useIntl()
  const { validate, validateField, errors, isValid } = useFormValidation(redeemSubscriptionSchema)
  const { applyDiscount, reduceBalance, isDiscounted } = useEdgeBalance()
  const [redeemStatus, setRedeemStatus] = useState<{
    status: 'warning' | 'success'
    message: string
  } | null>(null)

  const finalPrice = isDiscounted ? plan.cost * 0.5 : plan.cost

  const { mutate: redeemSubscription, isPending } =
    useRedemptionsRedemptionOptionsGtoWizardRedeemCreate({
      mutation: {
        onSuccess: (data) => {
          setRedeemStatus({
            status: data.success ? 'success' : 'warning',
            message: data.message,
          })

          reduceBalance(finalPrice)
          applyDiscount()
        },
        onError: (error: AxiosError) => {
          setRedeemStatus({
            status: 'warning',
            message:
              getAxiosErrorMessage(error) ||
              formatMessage({ id: 'edge.plansSection.redeem.failed' }),
          })
        },
      },
    })
  const [formValues, setFormValues] = useState<RedeemSubscriptionFormData>({
    gto_wizard_email: '',
  })

  const onSubmit = () => {
    const isValid = validate(formValues)

    setRedeemStatus(null)
    if (isValid) {
      redeemSubscription({
        data: { ...formValues, redemption_option_id: plan.id, expected_price: finalPrice },
      })
    }
  }

  const onChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setFormValues({
      gto_wizard_email: val,
    })

    validateField('gto_wizard_email', val)
  }

  // Reset the form on the open-edge during render (Base UI keeps the popup
  // mounted across open/close for the exit transition, so there's no unmount to
  // reset from and no lazy initializer to lean on). Done via the
  // store-previous-prop pattern rather than an effect or a remount key: it
  // clears status + email exactly when `open` flips true, commits no stale
  // frame, and — unlike a key — never remounts, so the close animation survives.
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      setRedeemStatus(null)
      setFormValues({
        gto_wizard_email: '',
      })
    }
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          onClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup className="w-full max-w-[500px] sm:top-[60px] sm:translate-y-0">
          <Dialog.Header>
            <FormattedMessage
              id={
                redeemStatus?.status === 'success'
                  ? 'edge.plansSection.redeem.success.title'
                  : 'edge.plansSection.redeemConfirmation.title'
              }
            />
          </Dialog.Header>
          <Dialog.Content>
            <p>
              {redeemStatus?.status === 'success' ? (
                redeemStatus.message
              ) : (
                <FormattedMessage id="edge.plansSection.redeemConfirmation.description" />
              )}
            </p>
            {redeemStatus?.status !== 'success' && (
              <Paper
                className="mt-4 rounded-md border border-primary-main p-8"
                elevation="1"
              >
                <div className="flex flex-col gap-4">
                  <GtoSubscriptionCardHeader
                    {...plan}
                    withSubGroup
                  />
                  <Divider flexItem />
                  <TextInput
                    error={errors.gto_wizard_email}
                    label="edge.plansSection.yourGtoWizardAccEmail"
                    input={{
                      size: 'medium',
                      name: 'gto_wizard_email',
                      onChange: onChangeEmail,
                      value: formValues.gto_wizard_email,
                    }}
                  />
                  {redeemStatus?.status === 'warning' && (
                    <Alert
                      heading={redeemStatus.message}
                      severity="warning"
                    />
                  )}
                </div>
              </Paper>
            )}
          </Dialog.Content>
          <Dialog.Footer>
            <Button
              color="secondary"
              disabled={isPending}
              size="large"
              variant="contained"
              onClick={onClose}
            >
              <FormattedMessage
                id={
                  redeemStatus?.status === 'success'
                    ? 'common.controls.close'
                    : 'common.controls.cancel'
                }
              />
            </Button>
            {redeemStatus?.status !== 'success' && (
              <Button
                color="primary"
                disabled={!isValid || isPending}
                loading={isPending}
                size="large"
                variant="contained"
                onClick={onSubmit}
              >
                <FormattedMessage id="common.controls.redeemNow" />
              </Button>
            )}
          </Dialog.Footer>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
